'use client';

import { useLandingTranslations } from './LandingLocaleProvider';
import { Reveal } from './Reveal';

const documents = [
  {
    titleKey: 'contextLayerDocument1Title',
    tags: ['proposal', 'architecture', 'desktop', 'runner'],
    tokenKey: 'contextLayerDocument1Tokens',
  },
  {
    titleKey: 'contextLayerDocument2Title',
    tags: ['runner', 'architecture', 'concurrency', 'quota'],
    tokenKey: 'contextLayerDocument2Tokens',
  },
  {
    titleKey: 'contextLayerDocument3Title',
    tags: ['runner', 'architecture', 'permission'],
    tokenKey: 'contextLayerDocument3Tokens',
  },
] as const;

const tags = [
  ['architecture', '28'],
  ['runner', '18'],
  ['proposal', '17'],
  ['research', '15'],
  ['needs-discussion', '12'],
] as const;

const features = [
  {
    titleKey: 'contextLayerFeature1Title',
    descKey: 'contextLayerFeature1Desc',
    icon: 'M4 6.5A2.5 2.5 0 016.5 4H20v14H6.5A2.5 2.5 0 014 15.5v-9zM4 6.5v9',
  },
  {
    titleKey: 'contextLayerFeature2Title',
    descKey: 'contextLayerFeature2Desc',
    icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  {
    titleKey: 'contextLayerFeature3Title',
    descKey: 'contextLayerFeature3Desc',
    icon: 'M13 10V3L4 14h7v7l9-11h-7z',
  },
] as const;

function TagIcon() {
  return (
    <svg aria-hidden="true" className="h-3 w-3" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 3.5v3.2L8.8 13l4.2-4.2-6.3-6.3H3.5a1 1 0 0 0-1 1Z" />
      <circle cx="5.2" cy="5.2" r=".8" fill="currentColor" stroke="none" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="11" cy="11" r="7" />
      <path strokeLinecap="round" d="m16.5 16.5 4 4" />
    </svg>
  );
}

function DocumentCard({
  title,
  tags: documentTags,
  tokens,
  t,
  isFavorite,
}: {
  title: string;
  tags: readonly string[];
  tokens: string;
  t: (key: string) => string;
  isFavorite?: boolean;
}) {
  return (
    <div className="rounded-xl border border-base-300 bg-base-100 px-4 py-3.5 shadow-xs">
      <div className="flex items-start justify-between gap-4">
        <p className="min-w-0 truncate text-sm font-semibold">{title}</p>
        <span className={`shrink-0 text-base ${isFavorite ? 'text-warning' : 'text-base-content/25'}`}>
          {isFavorite ? '★' : '☆'}
        </span>
      </div>
      <div className="mt-2.5 flex min-w-0 flex-wrap gap-1.5">
        {documentTags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1 text-[10px] font-medium text-primary"
          >
            <TagIcon />
            {tag}
          </span>
        ))}
      </div>
      <div className="mt-2.5 flex flex-wrap items-center gap-2 text-[10px] text-base-content/45">
        <span className="rounded-full bg-base-200 px-2 py-0.5">{t('contextLayerPrivate')}</span>
        <span className="rounded-full bg-base-200 px-2 py-0.5">{tokens}</span>
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-base-300 font-semibold text-base-content/60">
          J
        </span>
        <span>Justin</span>
        <span>·</span>
        <span>{t('contextLayerDocumentDate')}</span>
      </div>
    </div>
  );
}

function DocumentsMockup({ t }: { t: (key: string) => string }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-base-300 bg-base-100 shadow-sm">
      <div className="flex items-center justify-between border-b border-base-300 bg-base-200/35 px-5 py-3">
        <div className="flex items-center gap-2">
          <svg
            aria-hidden="true"
            className="h-4 w-4 text-primary"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.8}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a2 2 0 012-2h12a2 2 0 012 2v16l-8-4-8 4V5z" />
          </svg>
          <span className="text-sm font-semibold">{t('contextLayerLibraryTitle')}</span>
        </div>
        <span className="text-xs text-base-content/35">↻</span>
      </div>

      <div className="grid md:grid-cols-[190px_1fr]">
        <div className="border-b border-base-300 bg-base-200/20 p-4 md:border-b-0 md:border-r">
          <p className="mb-2 text-xs font-medium text-base-content/45">{t('contextLayerTagsLabel')}</p>
          <div className="flex items-center gap-2 rounded-lg border border-base-300 bg-base-100 px-3 py-2 text-xs text-base-content/35">
            <SearchIcon />
            <span>{t('contextLayerTagSearch')}</span>
          </div>
          <div className="mt-3 flex gap-2 overflow-hidden md:block">
            <div className="shrink-0 rounded-lg px-3 py-2 text-xs font-semibold md:mb-1">
              ☆ {t('contextLayerFavorites')}
            </div>
            <div className="shrink-0 rounded-lg px-3 py-2 text-xs font-semibold md:mb-2">
              {t('contextLayerAllDocuments')}
            </div>
            {tags.map(([tag, count]) => (
              <div
                key={tag}
                className={`flex shrink-0 items-center justify-between gap-4 rounded-lg px-3 py-2 text-xs ${
                  tag === 'runner' ? 'bg-primary/10 font-medium text-primary' : 'text-base-content/65'
                }`}
              >
                <span>{tag}</span>
                <span className="rounded-full bg-base-300/70 px-1.5 py-0.5 text-[10px] text-base-content/45">
                  {count}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="min-w-0 p-4">
          <div className="mb-3 flex items-center gap-2 rounded-xl border border-base-300 bg-base-200/25 px-3 py-2.5">
            <SearchIcon />
            <span className="text-xs text-base-content/35">{t('contextLayerDocumentSearch')}</span>
            <span className="ml-auto rounded-lg border border-base-300 bg-base-100 px-2 py-1 text-[10px] text-base-content/55">
              {t('contextLayerAllUsers')}
            </span>
          </div>

          <div className="space-y-2.5">
            {documents.map((document, index) => (
              <DocumentCard
                key={document.titleKey}
                title={t(document.titleKey)}
                tags={document.tags}
                tokens={t(document.tokenKey)}
                t={t}
                isFavorite={index < 2}
              />
            ))}
          </div>

          <div className="mt-3 rounded-xl border border-primary/15 bg-primary/5 px-4 py-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-semibold text-primary">{t('contextLayerRecoveryTitle')}</p>
                <p className="mt-0.5 text-[11px] text-base-content/50">{t('contextLayerRecoveryDescription')}</p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <span className="rounded-full bg-base-100 px-2 py-1 text-[10px] text-base-content/60">
                  {t('contextLayerRecoveryDocument')}
                </span>
                <span className="rounded-full bg-base-100 px-2 py-1 text-[10px] text-base-content/60">
                  {t('contextLayerRecoveryConvention')}
                </span>
                <span className="rounded-full bg-success/10 px-2 py-1 text-[10px] text-[var(--chip-success-fg)]">
                  ✓ {t('contextLayerRecoveryReady')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ContextLayerSection() {
  const t = useLandingTranslations();

  return (
    <Reveal>
      <section className="mt-16">
        <div className="mb-8 text-center">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            {t('contextLayerEyebrow')}
          </p>
          <h2 className="text-2xl font-bold md:text-3xl">{t('contextLayerTitle')}</h2>
          <p className="mx-auto mt-2 max-w-2xl text-sm leading-relaxed text-base-content/60">
            {t('contextLayerSubtitle')}
          </p>
        </div>

        <DocumentsMockup t={t} />

        <div className="mt-5 grid gap-4 md:grid-cols-3">
          {features.map((feature, index) => (
            <Reveal key={feature.titleKey} delay={index * 80}>
              <div className="h-full rounded-2xl border border-base-300 bg-base-100 p-5 shadow-xs">
                <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={feature.icon} />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold">{t(feature.titleKey)}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-base-content/60">{t(feature.descKey)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </Reveal>
  );
}
